import {inject, Injectable, NgZone, OnDestroy} from '@angular/core';
import {MessageService} from 'primeng/api';
import {UserService} from "../../../core/user.service";
import {User} from "../../../core/_datamodel/user";
import {catchError, forkJoin, map, Observable, of, ReplaySubject, Subscription, switchMap, tap, throwError} from "rxjs";
import {HttpClient, HttpErrorResponse, HttpHeaders} from "@angular/common/http";
import {environment} from "@app/environment";
import {CosoChat, CosoContext, CosoInteraction, CosoInteractionType, CosoStream} from "./datamodel";
import {CosoConfig, CosoConfigCapabilities} from "../coso-config/datamodel";
import {plainToInstance} from "class-transformer";

@Injectable({
  providedIn: 'root',
})
export class CosoService implements OnDestroy {
  public context$: ReplaySubject<CosoContext> = new ReplaySubject(1);
  cosoOptions: any = {
    // withCredentials: true,
    headers: new HttpHeaders().set('Content-Type', 'application/json')
      .set('api-key', environment.COSO_API_KEY)
      .set('ngsw-bypass','true')
  };
  backendOptions: any = {
    // withCredentials: true,
    headers: new HttpHeaders().set('Content-Type', 'application/json')
      .set('api-key', environment.API_KEY)
      .set('ngsw-bypass','true')
  };
  private messageService = inject(MessageService);
  private userService = inject(UserService);
  public user: User | null | undefined;
  private $userSub: Subscription | undefined;
  private currentContext: CosoContext = {
    project: environment.COSO_PROJECT_ID
  }

  private $allowedCosoConfigs: Subscription | undefined;
  public allowedCosoConfigs$: ReplaySubject<CosoConfig[]> = new ReplaySubject(1);
  public allowedCosoConfigs: CosoConfig[] = [];
  private defaultConfig: CosoConfig | undefined = undefined;

  constructor(private http: HttpClient, private ngZone: NgZone) {
    this.$userSub = this.userService.$user.subscribe({
      next: user => {
        if(user) {
          this.user = user;
          this.$allowedCosoConfigs?.unsubscribe();
          this.allowedCosoConfigs = [];
          this.allowedCosoConfigs$.next(this.allowedCosoConfigs);
          this.$allowedCosoConfigs = this.getAllowedCosoConfigs(this.user).subscribe({
            next: cosoConfigs => {
              this.allowedCosoConfigs = cosoConfigs;

              // $user emits multiple times (e.g. on cognito token refresh)
              // We only need to validate if the default config is still allowed
              if(this.defaultConfig && this.allowedCosoConfigs.find(c => c.coso_config_id === this.defaultConfig!.coso_config_id)) {
                this.allowedCosoConfigs$.next(this.allowedCosoConfigs);
                return;
              }

              // Defaults to the first config returned by the backend
              this.defaultConfig = this.allowedCosoConfigs[0];
              if (!this.defaultConfig) {                  // no configs allowed for this user
                this.allowedCosoConfigs$.next(this.allowedCosoConfigs);
                return;
              }

              this.currentContext = {
                project: this.defaultConfig!.project_id!,
                id_user: this.user!.user_id!,
                user_fullname: this.user!.fullname,
                id_group: this.user!.id_group,
                is_admin: !!this.user!.privileges["isAdmin"],
                id_coso_config: this.defaultConfig?.coso_config_id,
                module_name: this.defaultConfig?.module_name,
                language: 'it',
              }

              this.allowedCosoConfigs$.next(this.allowedCosoConfigs);
              this.context$.next(this.currentContext);

            },
            error: error => {
              this.messageService.add({severity: 'error', summary: 'Error', detail: error.message, sticky: true});
            }
          })
        } else {
          this.user = user;
          this.$allowedCosoConfigs?.unsubscribe();
          this.allowedCosoConfigs = [];
          this.allowedCosoConfigs$.next(this.allowedCosoConfigs);
        }
      },
      error: error => {
        this.messageService.add({severity: 'error', summary: 'Error', detail: error.message, sticky: true});
      }
    })
  }

  ngOnDestroy() {
    this.$userSub?.unsubscribe();
    this.$allowedCosoConfigs?.unsubscribe();
  }

  getAllowedCosoConfigs(user: User): Observable<CosoConfig[]> {
      return this.http.post(environment.API_URL + '/coso/getAllowedCosoConfigs', {user}, this.backendOptions).pipe(
          tap((data: any) => {
            if (!environment.production) console.debug('getAllowedCosoConfigs loaded:', data);
          }),
          map((data: any) => {
            // @ts-ignore
            return plainToInstance(CosoConfig, data) as unknown as CosoConfig[];
          }),
          switchMap((cosoConfigs: CosoConfig[]) => this.enrichCosoConfigsWithCapabilities(cosoConfigs)),
          catchError(err => {
            if (err.status === 401) {
              console.warn(err.error.message);
              return of(err);
            } else {
              return this.handleError(err);
            }
          })
      );
  }

  private enrichCosoConfigsWithCapabilities(cosoConfigs: CosoConfig[]): Observable<CosoConfig[]> {
    const projectIds = Array.from(new Set(
      cosoConfigs
        .map(config => config.project_id)
        .filter((projectId): projectId is string => !!projectId)
    ));

    if(!projectIds.length) {
      return of(cosoConfigs);
    }

    return forkJoin(projectIds.map(projectId => {
      return this.http.post(environment.COSO_API_URL + '/getFlowCapabilities', {project_id: projectId}, this.cosoOptions).pipe(
        map((capabilities: any) => capabilities as CosoConfigCapabilities),
        catchError(error => {
          console.warn(`Could not load COSO capabilities for project ${projectId}:`, error);
          return of(null);
        })
      );
    })).pipe(
      map(capabilitiesList => {
        const supportedMimeTypesByModel = new Map<string, string[]>();
        for(const capabilities of capabilitiesList) {
          if(!capabilities) {
            continue;
          }
          for(const provider of capabilities.providers ?? []) {
            for(const model of provider.models ?? []) {
              supportedMimeTypesByModel.set(`${capabilities.project_id}:${provider.id}:${model.model}`, model.supportedFileMimeTypes ?? []);
              supportedMimeTypesByModel.set(`${capabilities.project_id}:*:${model.model}`, model.supportedFileMimeTypes ?? []);
            }
          }
        }

        return cosoConfigs.map(config => {
          const supportedMimeTypes = supportedMimeTypesByModel.get(`${config.project_id}:${config.provider}:${config.model}`)
            ?? supportedMimeTypesByModel.get(`${config.project_id}:*:${config.model}`);

          if(supportedMimeTypes?.length) {
            config.supported_file_mime_types = supportedMimeTypes;
          }

          return config;
        });
      })
    );
  }

  getCurrentContextSnapshot(): CosoContext {
    return {...this.currentContext};
  }

  getDefaultCosoConfig(): CosoConfig | undefined {
    return this.allowedCosoConfigs.find(config => config.project_id === environment.COSO_PROJECT_ID)
      || this.allowedCosoConfigs[0];
  }

  updateCurrentContext(context: Partial<CosoContext>) {
    this.currentContext = {
      ...this.currentContext,
      ...context
    };
    this.context$.next(this.currentContext);
  }

  selectCosoChat(chat: CosoChat) {
    const config = this.allowedCosoConfigs.find(cosoConfig => cosoConfig.coso_config_id === chat.id_coso_config);

    this.updateCurrentContext({
      coso_chats_id: chat.coso_chats_id,
      id_user: chat.id_user ?? this.currentContext.id_user,
      id_coso_config: chat.id_coso_config ?? this.currentContext.id_coso_config,
      language: chat.language ?? this.currentContext.language ?? 'it',
      module_name: config?.module_name ?? this.currentContext.module_name,
      project: config?.project_id ?? this.currentContext.project
    });
  }

  getCosoChats(id_user: number): Observable<CosoChat[]> {
    return this.http.post(environment.COSO_API_URL + '/getCosoChats', {id_user}, this.cosoOptions).pipe(
      tap((data: any) => {
        if (!environment.production) console.debug('getCosoChats loaded:', data);
      }),
      map((data: any) => {
        // @ts-ignore
        return plainToInstance(CosoChat, data.records) as CosoChat[];
      }),
      catchError(err => {
        if (err.status === 401) {
          console.warn(err.error.message);
          return of([]);
        } else {
          return this.handleError(err);
        }
      })
    );
  }

  createCosoChat(chat: CosoChat): Observable<CosoChat> {
    return this.http.post(environment.COSO_API_URL + '/createCosoChat', chat, this.cosoOptions).pipe(
      tap((data: any) => {
        if (!environment.production) console.debug('createCosoChat loaded:', data);
      }),
      map((data: any) => {
        // @ts-ignore
        return plainToInstance(CosoChat, data);
      }),
      catchError(err => {
        if (err.status === 401) {
          console.warn(err.error.message);
          return of(err as CosoChat);
        } else {
          return this.handleError(err);
        }
      })
    );
  }

  updateCosoChat(chat: CosoChat): Observable<CosoChat> {
    return this.http.post(environment.COSO_API_URL + '/updateCosoChat', chat, this.cosoOptions).pipe(
      tap((data: any) => {
        if (!environment.production) console.debug('updateCosoChat loaded:', data);
      }),
      map((data: any) => {
        // @ts-ignore
        return plainToInstance(CosoChat, data);
      }),
      catchError(err => {
        if (err.status === 401) {
          console.warn(err.error.message);
          return of(err as CosoChat);
        } else {
          return this.handleError(err);
        }
      })
    );
  }

  deleteCosoChat(chat: CosoChat): Observable<boolean> {
    const options = {...this.cosoOptions, responseType: 'text' as const};
    return this.http.post(environment.COSO_API_URL + '/deleteCosoChat', chat, options).pipe(
      tap((data: any) => {
        if (!environment.production) console.debug('deleteCosoChat loaded:', data);
      }),
      map(() => true),
      catchError(err => {
        if (err.status === 401) {
          console.warn(err.error.message);
          return of(false);
        } else {
          return this.handleError(err);
        }
      })
    );
  }

  getMessageHistory(): Observable<CosoStream[]> {
    // @ts-ignore
    return this.http.post(environment.COSO_API_URL + '/getHistory', this.currentContext, this.cosoOptions).pipe(
      tap((data: any) => {
        if (!environment.production) {
          console.debug('getHistory:', data);
        }
      }),
      map((data: CosoStream[]) => {
        return data;
      }),
      catchError(err => {
        if (err.status === 401) {
          console.warn(err.error.message);
          return of([]);
        } else {
          return this.handleError(err);
        }
      })
    );
  }

  reloadFlows(): Observable<boolean> {
    // @ts-ignore
    return this.http.post(environment.COSO_API_URL + '/reloadFlows', this.currentContext, this.cosoOptions).pipe(
        tap((data: any) => {
          if (!environment.production) {
            console.debug('reloadFlows:', data);
          }
        }),
        switchMap(() => this.user ? this.getAllowedCosoConfigs(this.user) : of([])),
        tap(configs => {
          this.allowedCosoConfigs = configs;
          this.allowedCosoConfigs$.next(configs);
        }),
        map(() => true),
        catchError(err => {
          if (err.status === 401) {
            console.warn(err.error.message);
            return of(false);
          } else {
            return this.handleError(err);
          }
        })
    );
  }

  sendMessageToCosoStream(interaction: CosoInteraction): Observable<CosoStream> {
    return new Observable((observer) => {
      if (interaction.type === CosoInteractionType.MESSAGE && !interaction.content?.trim().length) {
        console.error('Error: trying to send message without text')
        console.error(interaction)
        observer.complete();
        return;
      }

      if (interaction.type === CosoInteractionType.INTERRUPT_RESPONSE && (!interaction.decisions || !interaction.decisions?.length)) {
        console.error('Error: trying to send interrupt response without decisions')
        console.error(interaction)
        observer.complete();
        return;
      }

      interaction.sessionId = this.user?.cognito_sub; // TODO: we still need it?
      interaction.userId = this.user!.user_id;
      interaction.context = this.currentContext;

      // Usiamo AbortController per permettere di cancellare la richiesta se l'utente fa unsubscribe
      const controller = new AbortController();
      const signal = controller.signal;

      this.userService.getJWT()
      .then((authToken) => {
        if (!authToken) {
          throw new Error('Missing Cognito JWT');
        }

        return fetch(environment.COSO_API_URL + '/sendMessageStream', {
          method: 'POST',
          headers: {
            'api-key': environment.COSO_API_KEY,
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(interaction),
          signal // Collega il segnale di abort
        });
      })
      .then(async (response) => {
        if (!response.ok || !response.body) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');
        let buffer = '';
        try {
          while (true) {
            const {value, done} = await reader.read();
            if (done) {
              this.ngZone.run(() => {
                observer.complete();
              });
              break;
            }
            // Decode current chunk and append to buffer
            buffer += decoder.decode(value, {stream: true});
            // Split by newline (NDJSON standard)
            const lines = buffer.split('\n');
            // The last line might be incomplete, so we keep it in the buffer
            buffer = lines.pop() || '';

            for (const line of lines) {
              if (line.trim()) {
                try {
                  this.ngZone.run(() => {
                    observer.next(JSON.parse(line))
                  });
                } catch (e) {
                  console.error('Error parsing JSON line:', e);
                }
              }
            }
          }
        } catch (err) {
          this.ngZone.run(() => {
            observer.error(err);
          });
        }
      })
      .catch((err) => {
        // Gestione errori della fetch (es. rete)
        if (err.name === 'AbortError') {
          // Richiesta annullata, non fare nulla
        } else {
          this.ngZone.run(() => observer.error(err));
        }
      });

      // Questa viene chiamata quando fai .unsubscribe()
      return () => {
        controller.abort();
      };
    });
  }

  private handleError(err: HttpErrorResponse) {
    // in a real world app, we may send the server to some remote logging infrastructure
    // instead of just logging it to the console
    let errorMessage;
    //if (err.error instanceof Error) {
    if (err.error instanceof Error) {
      // A client-side or network error occurred. Handle it accordingly.
      errorMessage = `An error occurred: ${err.error.message}`;
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong,
      errorMessage = err.error.message || err.error.statusText;
    }
    console.error(errorMessage);
    return throwError(() => {
      return new Error(errorMessage)
    });
  }
}
